import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import Layout from "../../components/Layout";
import EntityHeader from "../../components/crm/EntityHeader";
import { ErrorBanner, Select } from "../../components/crm/Form";
import api, { formatApiError } from "../../lib/api";

const MFR_OPTIONS = [
  { value: "qcells", label: "QCells" },
  { value: "silfab", label: "Silfab" },
  { value: "mission", label: "Mission" },
  { value: "rec", label: "REC" },
  { value: "others", label: "Others" },
];

export default function EquipmentList() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const systemId = params.get("solar_system_id") || "";
  const mfr = params.get("manufacturer") || "";

  const [list, setList] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const filters = {};
      if (systemId) filters.solar_system_id = systemId;
      if (mfr) filters.manufacturer = mfr;
      const { data } = await api.get("/equipment/", { params: filters });
      setList(data);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [systemId, mfr]);

  const setMfr = (val) => {
    const next = new URLSearchParams(params);
    if (val) next.set("manufacturer", val);
    else next.delete("manufacturer");
    setParams(next, { replace: true });
  };

  return (
    <Layout title="Equipment" subtitle="CRM">
      <EntityHeader
        overline="hardware"
        title="Equipment"
        subtitle="Panels, inverters, batteries, and other tracked components."
      />

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="w-44">
          <Select
            value={mfr}
            onChange={(v) => setMfr(v)}
            options={MFR_OPTIONS}
            placeholder="All manufacturers"
            data-testid="equipment-mfr-filter"
          />
        </div>
        {systemId && (
          <div className="font-data text-[10px] uppercase tracking-[0.18em] text-slate-600 bg-amber-50 border border-amber-200 px-3 py-2 rounded-sm">
            filter · system_id {systemId.slice(0, 8)}
          </div>
        )}
      </div>

      <ErrorBanner message={err} />

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm" data-testid="equipment-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="font-data text-[10px] uppercase tracking-[0.18em] text-slate-600">
              <th className="text-left px-5 py-3 font-bold">Manufacturer</th>
              <th className="text-left px-5 py-3 font-bold">Model</th>
              <th className="text-left px-5 py-3 font-bold">Serial</th>
              <th className="text-left px-5 py-3 font-bold">Installed</th>
              <th className="text-left px-5 py-3 font-bold">Warranty</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {!loading && list.length === 0 && (
              <tr>
                <td
                  colSpan={5}
                  className="px-5 py-12 text-center text-sm text-slate-500"
                  data-testid="equipment-empty"
                >
                  No equipment yet.
                </td>
              </tr>
            )}
            {list.map((e) => (
              <tr
                key={e.id}
                className="cursor-pointer hover:bg-slate-50/60"
                onClick={() => nav(`/equipment/${e.id}`)}
                data-testid={`equipment-row-${e.id}`}
              >
                <td className="px-5 py-3 font-medium text-slate-900">
                  {e.manufacturer}
                </td>
                <td className="px-5 py-3 text-xs text-slate-700">{e.model}</td>
                <td className="px-5 py-3 font-data text-xs text-slate-700">
                  {e.serial_number}
                </td>
                <td className="px-5 py-3 font-data text-xs text-slate-700 tabular">
                  {e.install_date || "—"}
                </td>
                <td className="px-5 py-3 font-data text-xs text-slate-700 tabular">
                  {e.warranty_expires || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Layout>
  );
}
